import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface HeroSectionProps {
    highlightColor: {
        primary: string;
        hover: string;
        gradient: {
            from: string;
            to: string;
            fromBlue: string;
            toPurple: string;
        };
    };
    onNewsClick: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ highlightColor, onNewsClick }) => {
    const navigate = useNavigate();
    const [showVideoModal, setShowVideoModal] = useState(false);
    const videoRef = React.useRef<HTMLVideoElement>(null);

    const handleOpenVideoModal = () => setShowVideoModal(true);
    const handleCloseVideoModal = () => setShowVideoModal(false);
    const handleFocusVideo = () => {
        if (videoRef.current) {
            videoRef.current.focus();
            videoRef.current.play();
        }
    };

    return (
        <section className="px-4 sm:px-6 lg:px-8 relative overflow-hidden min-h-screen flex items-start bg-black">
            {/* Video Background */}
            <video
                ref={videoRef}
                className="absolute inset-0 w-full h-full object-cover z-0"
                src="/videos/home-hero-background.mp4"
                autoPlay
                loop
                muted
                playsInline
            />
            {/* Video Focus/Play Button - Upper Right */}
            <button
                type="button"
                onClick={handleFocusVideo}
                className="absolute top-4 right-4 z-20 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white transition-all duration-300 ease-out hover:scale-110 focus:outline-none"
                title="Focus Video"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            </button>
            <div className="absolute inset-0 bg-black/50 z-0"></div>
            
            <div className="max-w-7xl mx-auto relative z-10 pt-24 md:pt-32">
                {/* CTA Button */}
                <div className="space-y-8">
                    <h1 className="text-5xl md:text-7xl leading-tight observe-fade opacity-0">
                        <span className="text-white drop-shadow-lg">Connect.</span>
                        <br />
                        <span className="text-white drop-shadow-lg">Analyze.</span>
                        <br />
                        <span 
                            className="bg-clip-text text-transparent drop-shadow-lg"
                            style={{
                                backgroundImage: `linear-gradient(to right, ${highlightColor.gradient.fromBlue}, ${highlightColor.gradient.toPurple})`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                            }}
                        >
                            Compete.
                        </span>
                    </h1>
                    <p className="text-lg md:text-xl text-white leading-relaxed max-w-4xl observe-fade opacity-0 drop-shadow-lg">
                        Centralize training, recovery, and readiness data in one platform built for your system. 
                    </p>
                    <p className="text-lg text-gray-200 leading-relaxed max-w-4xl observe-fade opacity-0 drop-shadow-lg">
                        CGM Sports is an intelligent performance layer that learns from your team’s data—delivering 
                        predictive insights to help coaches anticipate performance, optimize training load, and keep 
                        athletes ready.
                    </p>
                </div>
                <div className="mt-8 observe-fade opacity-0">
                    <button
                        onClick={handleOpenVideoModal}
                        className="inline-flex items-center space-x-2 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all duration-300 hover:scale-105"
                        style={{
                            background: `linear-gradient(to right, #2563eb, ${highlightColor.gradient.to})`
                        }}
                    >
                        <span className="font-semibold">Interested?</span>
                        <span>Click here to see more.</span>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Video Modal */}
            {showVideoModal && (
                <div 
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 backdrop-blur-sm p-8 md:p-16 animate-in fade-in duration-300"
                    onClick={handleCloseVideoModal}
                >
                    <div 
                        className="relative w-full max-w-7xl aspect-video rounded-xl overflow-hidden shadow-2xl border border-gray-700 bg-black flex items-center justify-center animate-in zoom-in-95 duration-300"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <video
                            className="w-full h-full object-contain"
                            src="/videos/home-hero-modal-demo.mp4"
                            autoPlay
                            loop
                            muted
                            playsInline
                            controls
                        />
                        <button
                            type="button"
                            onClick={handleCloseVideoModal}
                            className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white transition-all duration-300 ease-out hover:scale-110 focus:outline-none"
                            title="Close Fullscreen"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
};

export default HeroSection;

